import axios, { type AxiosResponse } from "axios";
import { parseLineMeta } from "./log-format";
import { io, type Socket } from "socket.io-client";
import {
  type Coordinates,
  type Dimensions,
  type MediaFile,
  type SimulatorSessionInfo,
  type TestSuiteData,
} from "./types";
import { atomWithMutation } from "jotai-tanstack-query";
import {
  getDeviceHubUrl,
  poll,
  retryOperation,
  uint8ArrayToBase64,
  updateDeviceHubServerStatus,
} from "./helpers";
import { type Subscriber } from "rxjs";
import { Store } from "@tanstack/store";
import { DateTime } from "luxon";

interface Device {
  screen: Dimensions;
  platform: string;
  os?: string;
  name?: string;
}

interface SimulatorSession {
  stop: () => Promise<void>;
  checkIfSessionIsRunning: () => Promise<boolean>;
  getLogsStore: () => Store<any[]>;
  getNetworkLogsStore: () => Store<any[]>;
  getUI: () => Promise<any>;
  getScreenshot: () => Promise<string>;
  device: Device;
  startedAt: number;
  info: SimulatorSessionInfo;
  serverUrl: string;
  sessionId: string;
  launchApp: () => Promise<void>;
  commands: {
    biometry: (match: boolean) => Promise<void>;
    tap: ({
      coordinates,
      duration,
    }: {
      coordinates: { x: number; y: number };
      duration: number;
    }) => Promise<void>;
    multiTap: ({
      coordinates,
      count,
    }: {
      coordinates: { x: number; y: number };
      count: number;
    }) => Promise<void>;
    doubleTap: ({
      coordinates,
      duration,
      count,
    }: {
      coordinates: { x: number; y: number };
      duration: number;
      count: number;
    }) => Promise<void>;
    longPress: ({
      coordinates,
      duration,
      count,
    }: {
      coordinates: { x: number; y: number };
      duration: number;
      count: number;
    }) => Promise<void>;
    setLocation: (coordinates: Coordinates) => Promise<void>;
    zoom: (direction: "in" | "out") => Promise<void>;
    playAudio: (url: string) => Promise<void>;
    playVoice: (text: string) => Promise<string>;
    listen: () => Promise<{ text: string; audioUrl: string }>;
    checkAudio: (seconds?: number) => Promise<boolean>;
    tapOnElement: ({
      text,
      id,
      duration,
    }: {
      text?: string;
      id?: string;
      duration: number;
    }) => Promise<Object | null>;
    type: ({ text }: { text: string }) => Promise<void>;
    keyStroke: ({ keycode }: { keycode: number }) => Promise<void>;
    openDeepLinkUrl: (url: string) => Promise<void>;
    openAppClipLink: (url: string) => Promise<void>;
    swipe: ({
      startCoordinates,
      endCoordinates,
      duration,
    }: {
      startCoordinates: { x: number; y: number };
      endCoordinates: { x: number; y: number };
      duration?: number;
    }) => Promise<void>;
    tapHomeScreen: () => Promise<void>;
    restartApp: () => Promise<void>;
    terminateApp: () => Promise<void>;
    shakeDevice: () => Promise<void>;
  };
  setSubscriber: (subscription: Subscriber<unknown>) => void;
  subscriber?: Subscriber<unknown>;
  on: (
    event: "video",
    callback: (payload: VideoFramePayload) => void
  ) => SimulatorSession;
  off: (
    event: "video",
    callback: (payload: VideoFramePayload) => void
  ) => SimulatorSession;
}

interface CreateSessionMutationAtomParams {
  platform: string;
  device: string;
  os: string;
  buildId: string;
  bundleId: string;
  storagePath: string;
  language?: string;
  countryCode?: string;
  grantPermissions?: boolean;
  proxy?: string;
  recordVideo?: boolean;
  onCleanup: () => void;
  organisationId: string;
  testSuite?: TestSuiteData;
  testRunId?: string;
  launchParams?: Array<{ key: string; value: string }>;
  mediaFiles?: MediaFile[];
  userId: string;
  usePhysicalDevice: boolean;
  subscriber?: Subscriber<unknown>;
}

// Socket.IO event types
interface FrameMetadata {
  timestamp: number;
  frameNumber: number;
  originalSize: {
    width: number;
    height: number;
  };
}

interface VideoFramePayload {
  buffer: Uint8Array;
  width: number;
  height: number;
  codec: "jpeg";
}

interface LogMessage {
  timestamp: number;
  type: string;
  message: string;
}

interface ServerToClientEvents {
  frame: (frameData: ArrayBuffer, metadata: FrameMetadata) => void;
  log_batch: (batch: LogMessage[]) => void;
  network_capture_batch: (batch: any[]) => void;
  error: (message: string) => void;
  connect: () => void;
  disconnect: () => void;
}

interface ClientToServerEvents {
  "start-stream": (roomId: string) => void;
  "stop-stream": () => void;
}

const sessionMutationAtom = atomWithMutation((get) => ({
  mutationKey: ["createSessionMutation"],
  mutationFn: createSimulatorSession,
}));

// Helper function to check if Socket.IO streaming is available
async function checkSocketIOHealth(instanceIp: string): Promise<boolean> {
  try {
    const healthCheckUrl = `${instanceIp}/stream/health`;
    console.log(`Checking Socket.IO health at: ${healthCheckUrl}`);

    const response = await axios.get(healthCheckUrl, {
      timeout: 5000, // 5 second timeout
      validateStatus: (status) => status === 200, // Only accept 200 as success
    });

    console.log("Socket.IO health check passed, using Socket.IO for streaming");
    return true;
  } catch (error) {
    console.log("Socket.IO health check failed, falling back to WebRTC", error);
    return false;
  }
}

// Helper function to fetch TURN credentials
async function getTurnCredentials(
  instanceIp: string,
  sessionId: string
): Promise<RTCIceServer[]> {
  const turnCredentialsURL = `${instanceIp}/turn-credentials/${sessionId}`;
  try {
    console.log(
      `Fetching TURN server credentials from ${turnCredentialsURL}...`
    );
    const response = await axios.get(turnCredentialsURL);
    if (response.status === 200 && response.data && response.data.iceServers) {
      console.log("Successfully fetched TURN credentials.");
      return response.data.iceServers;
    } else {
      throw new Error(
        `Failed to fetch TURN credentials: ${response.status} ${
          response.data?.detail || response.statusText
        }`
      );
    }
  } catch (error: any) {
    console.error("Error fetching TURN credentials:", error.message);
    console.log("Using public STUN as fallback due to TURN fetch error.");
    return [{ urls: "stun:stun.l.google.com:19302" }]; // Fallback
  }
}

// Create a cleanup function to handle session termination
const cleanupSession = async ({
  sessionId,
  socket,
  pc,
  serverUrl,
  useSocketIO,
  organisationId,
  onCleanup,
  subscriber,
  storesToReset,
}: {
  sessionId: string | null;
  socket: Socket | null;
  pc: RTCPeerConnection | null;
  serverUrl: string;
  useSocketIO: boolean;
  organisationId: string;
  onCleanup: () => void;
  subscriber?: Subscriber<unknown>;
  storesToReset?: Array<Store<any>>;
}) => {
  try {
    // Stop session on server if session ID exists
    if (sessionId) {
      const deviceHubUrl = getDeviceHubUrl(organisationId);
      await axios.post(`${deviceHubUrl}/stop_session`, {
        session_id: sessionId,
        ip_address: serverUrl,
      });
    }
  } catch (error) {
    await updateDeviceHubServerStatus(serverUrl, "Error", organisationId);
    console.error("Error during session cleanup:", error);
    throw error;
  } finally {
    storesToReset?.forEach((store) => {
      store.setState(() => []);
    });
    if (subscriber) {
      subscriber.error({
        type: "VIDEO_STREAMING_ERROR",
      });
    }
    onCleanup();
    if (useSocketIO && socket != null) {
      // Clean up Socket.IO
      socket.emit("stop-stream");
      socket.disconnect();

      // Remove canvas element
      const deviceWrapper = document.getElementById("deviceWrapper");
      const canvasElement = deviceWrapper?.querySelector("canvas");
      if (canvasElement != null) {
        deviceWrapper?.removeChild(canvasElement);
      }
    } else if (!useSocketIO && pc != null) {
      // Clean up WebRTC
      pc.close();

      // Remove video element
      const deviceWrapper = document.getElementById("deviceWrapper");
      const videoElement = deviceWrapper?.querySelector("video");
      if (videoElement != null) {
        videoElement.srcObject = null;
        deviceWrapper?.removeChild(videoElement);
      }
    }
  }
};

const createSimulatorSession = async ({
  platform,
  device,
  os,
  buildId,
  bundleId,
  language,
  grantPermissions,
  countryCode,
  proxy,
  recordVideo = true,
  onCleanup,
  organisationId,
  testRunId,
  testSuite,
  launchParams,
  mediaFiles,
  userId,
  usePhysicalDevice,
  subscriber,
}: CreateSessionMutationAtomParams) => {
  let sessionId: string | null = null;
  let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  let pc: RTCPeerConnection | null = null;
  let useSocketIO = false;
  let reconnectionAttempts = 0;
  const MAX_RECONNECTION_ATTEMPTS = 3;
  const RECONNECTION_DELAY = 5000; // 5 seconds
  let reconnectionTimer: NodeJS.Timeout | null = null;
  let nonNullSubscriber = subscriber;

  let sessionResponse: AxiosResponse | undefined;

  await retryOperation(
    async (_) => {
      try {
        const deviceHubUrl = getDeviceHubUrl(organisationId);

        // Convert launch params array to object
        const launchParamsObj = launchParams?.reduce((acc, param) => {
          acc[param.key] = param.value;
          return acc;
        }, {} satisfies Record<string, string>);

        sessionResponse = await axios.post(`${deviceHubUrl}/start_session`, {
          device_name: device,
          osVersion: os,
          build_id: buildId,
          bundle_id: bundleId,
          language,
          locale: countryCode,
          grant_permissions: grantPermissions,
          save_recording: recordVideo,
          proxy,
          organisation_id: organisationId,
          test_suite_run_id: testSuite?.id,
          run_id: testRunId,
          ...(launchParamsObj && { launch_params: launchParamsObj }),
          user_id: userId,
          use_physical_device: usePhysicalDevice,
          // ip_address: "https://par-1q.mobileboost.io",
        });
      } catch (e: any) {
        if (e?.response?.data?.detail === "INVALID_DEVICE_CONFIGURATION") {
          throw { ...e, cancelRetry: true };
        } else if (
          e?.response?.status === 400 &&
          e?.response?.data?.detail ===
            ".ipa files are not supported on simulators"
        ) {
          throw {
            ...e,
            message: e?.response?.data?.detail,
            cancelRetry: true,
          };
        } else if (e?.response?.status === 404) {
          throw {
            ...e,
            message:
              "No devices available for your account right now. Please try again shortly",
            cancelRetry: true,
          };
        } else if (e?.response?.status === 429) {
          throw {
            ...e,
            message:
              "The limit of parallel devices on your account has been reached. Please close other connections and try again.",
            cancelRetry: true,
          };
        }
        throw e;
      }
    },
    10000,
    3
  );

  if (sessionResponse === undefined) {
    console.error("Failed to obtain session response after retries.");
    throw new Error("Failed to obtain session response after retries.");
  }

  sessionId = sessionResponse.data.session_id;
  const instanceIp: string = sessionResponse.data.instanceIp;

  // Check if Socket.IO streaming is available
  useSocketIO = await checkSocketIOHealth(instanceIp);

  const launchApp = async () => {
    await axios.get(`${instanceIp}/interaction/launch_app/${sessionId}`);
  };

  try {
    const sessionInfo = await poll<SimulatorSessionInfo>(
      `${instanceIp}/session/${sessionId}`,
      (response) => {
        return response.data.state === "assigned";
      },
      5,
      25,
      axios
    );

    // Push media files to device before app launch
    if (mediaFiles && mediaFiles.length > 0) {
      const { pushMediaToGateway } = await import("./media-helpers");
      await pushMediaToGateway(instanceIp, sessionId!, mediaFiles);
    }

    if (platform.toLowerCase() === "android") {
      await launchApp();
    }

    const isCloudExecution = testRunId != null;

    // Branch based on health check result
    let latestSocketIOScreenshot: ArrayBuffer | null = null;
    const videoFrameListeners = new Set<(payload: VideoFramePayload) => void>();
    const emitVideoFrame = (payload: VideoFramePayload) => {
      videoFrameListeners.forEach((listener) => listener(payload));
    };

    const logsStore = new Store<
      Array<{
        id: number;
        timestamp: string;
        level: string;
        process: string;
        message: string;
      }>
    >([]);
    const networkLogsStore = new Store<any[]>([]);

    if (useSocketIO) {
      // ============ SOCKET.IO SECTION ============
      const socketUrl = instanceIp.replace(/^https?:\/\//, "");

      console.log(`[Socket.IO] Initializing connection to: wss://${socketUrl}`);
      console.log(`[Socket.IO] Session ID (room): ${sessionId}`);

      socket = io(`https://${socketUrl}`, {
        path: "/stream/socket.io/",
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
      });

      // Track frame reception for debugging
      let frameCount = 0;
      let lastFrameTime = Date.now();
      let frameTimings: number[] = [];

      socket.on("connect", () => {
        const now = new Date().toISOString();
        console.log(`[Socket.IO] ✅ Connected at ${now}`);
        console.log(`[Socket.IO] Socket ID: ${socket.id}`);
        console.log(
          `[Socket.IO] Transport: ${socket.io.engine.transport.name}`
        );

        frameCount = 0;
        lastFrameTime = Date.now();

        if (sessionId) {
          console.log(
            `[Socket.IO] 📤 Emitting 'start-stream' with room: ${sessionId}`
          );
          socket.emit("start-stream", sessionId);
        }
      });

      socket.on("disconnect", (reason) => {
        const now = new Date().toISOString();
        console.log(`[Socket.IO] ❌ Disconnected at ${now}`);
        console.log(`[Socket.IO] Reason: ${reason}`);
        console.log(`[Socket.IO] Total frames received: ${frameCount}`);

        if (frameTimings.length > 0) {
          const avgInterval =
            frameTimings.reduce((a, b) => a + b, 0) / frameTimings.length;
          console.log(
            `[Socket.IO] Average frame interval: ${avgInterval.toFixed(2)}ms`
          );
        }
      });

      socket.io.on("reconnect_attempt", (attemptNumber: number) => {
        console.log(`[Socket.IO] 🔄 Reconnection attempt ${attemptNumber}`);
      });

      socket.io.on("reconnect", (attemptNumber: number) => {
        console.log(
          `[Socket.IO] ✅ Reconnected after ${attemptNumber} attempts`
        );

        frameCount = 0;
        lastFrameTime = Date.now();
        frameTimings = [];

        if (sessionId) {
          console.log(
            `[Socket.IO] 📤 Re-emitting 'start-stream' with room: ${sessionId}`
          );
          socket.emit("start-stream", sessionId);
        }
      });

      socket.io.on("reconnect_error", (error: Error) => {
        console.error(`[Socket.IO] ⚠️ Reconnection error:`, error.message);
      });

      socket.io.on("reconnect_failed", () => {
        console.error(
          `[Socket.IO] 💀 Failed to reconnect after maximum attempts`
        );
        console.log(
          `[Socket.IO] Total frames received before failure: ${frameCount}`
        );

        cleanupSession({
          sessionId,
          socket,
          pc: null,
          serverUrl: instanceIp,
          useSocketIO,
          organisationId,
          onCleanup,
          subscriber: nonNullSubscriber,
          storesToReset: [logsStore, networkLogsStore],
        });
      });

      socket.on(
        "frame",
        async (frameData: ArrayBuffer, metadata: FrameMetadata) => {
          frameCount++;
          const now = Date.now();
          const timeSinceLastFrame = now - lastFrameTime;

          if (frameCount === 1) {
            console.log(`[Socket.IO] 🎬 First frame received!`);
          }

          // Log every 300 frames to avoid spam
          if (frameCount % 300 === 0) {
            const avgInterval =
              frameTimings.length > 0
                ? frameTimings.reduce((a, b) => a + b, 0) / frameTimings.length
                : 0;
            console.log(
              `[Socket.IO] 📊 Frame #${frameCount} | Interval: ${timeSinceLastFrame}ms | Avg: ${avgInterval.toFixed(
                2
              )}ms | Size: ${(frameData.byteLength / 1024).toFixed(2)}KB`
            );
          }

          frameTimings.push(timeSinceLastFrame);
          if (frameTimings.length > 100) frameTimings.shift();

          lastFrameTime = now;

          latestSocketIOScreenshot = frameData;

          const deviceWrapper = document.getElementById("deviceWrapper");
          if (!deviceWrapper) {
            console.warn(`[Socket.IO] ⚠️ deviceWrapper not found in DOM`);
            return;
          }

          let canvasElement = deviceWrapper.querySelector(
            "canvas"
          ) as HTMLCanvasElement;

          if (!canvasElement) {
            console.log(`[Socket.IO] 🎨 Creating canvas element`);
            canvasElement = document.createElement("canvas");
            canvasElement.style.maxWidth = "100%";
            canvasElement.style.maxHeight = "100%";
            canvasElement.style.height = "100%";
            canvasElement.style.marginInline = "auto";
            canvasElement.style.display = "none";
            canvasElement.style.imageRendering = "crisp-edges";
            deviceWrapper.appendChild(canvasElement);
          }

          try {
            const blob = new Blob([frameData], { type: "image/jpeg" });
            const imageBitmap = await createImageBitmap(blob);
            emitVideoFrame({
              buffer: new Uint8Array(frameData),
              width: imageBitmap.width,
              height: imageBitmap.height,
              codec: "jpeg",
            });

            const ctx = canvasElement.getContext("2d");
            if (!ctx) {
              console.error(`[Socket.IO] ❌ Failed to get canvas context`);
              return;
            }

            if (
              canvasElement.width !== imageBitmap.width ||
              canvasElement.height !== imageBitmap.height
            ) {
              console.log(
                `[Socket.IO] 📐 Canvas resized: ${imageBitmap.width}x${imageBitmap.height}`
              );
              canvasElement.width = imageBitmap.width;
              canvasElement.height = imageBitmap.height;
            }

            ctx.drawImage(imageBitmap, 0, 0);
          } catch (err) {
            console.error(`[Socket.IO] ❌ Error rendering frame:`, err);
          }
        }
      );

      socket.on("error", (message: string) => {
        console.error(`[Socket.IO] ❌ Stream error:`, message);
      });

      socket.on("log_batch", (batch: LogMessage[]) => {
        const isIos = platform.toLowerCase() === "ios";
        logsStore.setState((prev) => [
          ...prev,
          ...batch.map((msg, i) => {
            const { level, process, message } = parseLineMeta(
              msg.message,
              isIos
            );
            return {
              id: prev.length + i,
              timestamp: DateTime.fromSeconds(msg.timestamp).toFormat(
                "hh:mm:ss"
              ),
              level,
              process,
              message,
            };
          }),
        ]);
      });

      socket.on("network_capture_batch", (batch: any[]) => {
        networkLogsStore.setState((prev) => [
          ...prev,
          ...batch.map((m) => m.message),
        ]);
      });

      socket.io.on("ping", () => {
        console.log(`[Socket.IO] 🏓 Ping sent`);
      });

      socket.io.on("pong", (latency: number) => {
        console.log(`[Socket.IO] 🏓 Pong received (latency: ${latency}ms)`);
      });

      // Wait for Socket.IO connection
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error(`[Socket.IO] ⏱️ Connection timeout after 10s`);
          reject(new Error("Socket.IO connection timeout"));
        }, 10000);

        if (socket!.connected) {
          console.log(`[Socket.IO] Already connected`);
          clearTimeout(timeout);
          resolve();
        } else {
          socket!.once("connect", () => {
            console.log(`[Socket.IO] Connection promise resolved`);
            clearTimeout(timeout);
            resolve();
          });

          socket!.once("connect_error", (error) => {
            console.error(`[Socket.IO] Connection error during setup:`, error);
            clearTimeout(timeout);
            reject(error);
          });
        }
      });

      console.log(`[Socket.IO] 🎉 Setup complete, waiting for frames...`);
    } else {
      // ============ WEBRTC SECTION ============
      console.log(`[WebRTC] Initializing WebRTC connection`);

      let iceServersConfig: RTCIceServer[];
      if (sessionId) {
        iceServersConfig = await getTurnCredentials(instanceIp, sessionId);
      } else {
        console.warn(
          `[WebRTC] Session ID is null. Using public STUN server as fallback.`
        );
        iceServersConfig = [{ urls: "stun:stun.l.google.com:19302" }];
      }

      if (!iceServersConfig || iceServersConfig.length === 0) {
        console.error(`[WebRTC] Could not obtain ICE server configurations.`);
      }
      console.log(`[WebRTC] Using ICE Servers:`, iceServersConfig);

      pc = new RTCPeerConnection({
        iceServers: iceServersConfig,
      });

      const attemptReconnection = async () => {
        if (reconnectionAttempts >= MAX_RECONNECTION_ATTEMPTS) {
          console.log(
            `[WebRTC] Max reconnection attempts reached, cleaning up session`
          );
          await cleanupSession({
            sessionId,
            socket: null,
            pc,
            serverUrl: instanceIp,
            useSocketIO,
            organisationId,
            onCleanup,
            subscriber: nonNullSubscriber,
            storesToReset: [logsStore, networkLogsStore],
          });
          return;
        }

        reconnectionAttempts++;
        console.log(
          `[WebRTC] Attempting reconnection (${reconnectionAttempts}/${MAX_RECONNECTION_ATTEMPTS})`
        );

        reconnectionTimer = setTimeout(async () => {
          if (pc.connectionState !== "connected") {
            await attemptReconnection();
          }
        }, RECONNECTION_DELAY);
      };

      if (!isCloudExecution) {
        pc.onconnectionstatechange = async () => {
          console.log(
            `[WebRTC] Connection state changed: ${pc.connectionState}`
          );

          if (reconnectionTimer != null) {
            clearTimeout(reconnectionTimer);
            reconnectionTimer = null;
          }

          switch (pc.connectionState) {
            case "disconnected":
              console.log(
                `[WebRTC] Connection disconnected, attempting to reconnect`
              );
              await attemptReconnection();
              break;

            case "failed":
              console.log(
                `[WebRTC] Connection failed, attempting emergency reconnection`
              );
              reconnectionAttempts = MAX_RECONNECTION_ATTEMPTS - 1;
              await attemptReconnection();
              break;

            case "closed":
              console.log(`[WebRTC] Connection closed, cleaning up`);
              await cleanupSession({
                sessionId,
                socket: null,
                pc,
                serverUrl: instanceIp,
                useSocketIO,
                organisationId,
                onCleanup,
                subscriber: nonNullSubscriber,
                storesToReset: [logsStore, networkLogsStore],
              });
              break;

            case "connected":
              console.log(`[WebRTC] ✅ Connection established successfully`);
              reconnectionAttempts = 0;
              break;
          }
        };
      }

      pc.ontrack = function (event) {
        console.log(`[WebRTC] Track event received:`, event.track.kind);

        event.streams.forEach((stream) => {
          stream.getTracks().forEach((track) => {
            console.log(
              `[WebRTC] Track kind: ${track.kind}, enabled: ${track.enabled}`
            );

            if (track.kind === "video") {
              const videoElement = document.createElement("video");
              videoElement.srcObject = stream;
              videoElement.autoplay = true;
              videoElement.controls = false;
              videoElement.playsInline = true;
              videoElement.preload = "none";
              videoElement.playbackRate = 1.0;

              videoElement.addEventListener("loadedmetadata", () => {
                console.log(`[WebRTC] Video metadata loaded`);
                console.log(
                  `[WebRTC] Video dimensions: ${videoElement.videoWidth}x${videoElement.videoHeight}`
                );

                videoElement.style.maxWidth = "100%";
                videoElement.style.maxHeight = "100%";
                videoElement.style.height = "100%";
                videoElement.style.marginInline = "auto";

                const deviceWrapperElement =
                  document.getElementById("deviceWrapper");
                const existingVideoChild =
                  deviceWrapperElement?.querySelector("video");
                if (existingVideoChild != null) {
                  deviceWrapperElement?.removeChild(existingVideoChild);
                }
                deviceWrapperElement?.appendChild(videoElement);
              });

              videoElement.addEventListener("error", (e) => {
                console.error(`[WebRTC] Video element error:`, e);
              });
            }
          });
        });
      };

      pc.addTransceiver("video", { direction: "recvonly" });
      const localDescription = await pc.createOffer();
      await pc.setLocalDescription(localDescription);
      console.log(
        `[WebRTC] Offer created, set local description. Gathering ICE candidates...`
      );

      // Wait for ICE gathering to complete
      await new Promise<void>((resolve) => {
        if (pc.iceGatheringState === "complete") {
          resolve();
        } else {
          const checkState = () => {
            if (pc.iceGatheringState === "complete") {
              pc.removeEventListener("icegatheringstatechange", checkState);
              resolve();
            }
          };
          pc.addEventListener("icegatheringstatechange", checkState);
        }
      });

      console.log(
        `[WebRTC] ICE gathering complete. Preparing complete offer for server...`
      );

      const completeOfferSdpObject = pc.localDescription?.toJSON();
      if (!completeOfferSdpObject) {
        throw new Error(
          "Failed to generate complete localDescription for SDP offer after ICE gathering."
        );
      }
      const base64EncodedCompleteOffer = btoa(
        JSON.stringify(completeOfferSdpObject)
      );

      const serverDescriptionResponse = await axios.post(`${instanceIp}/sdp/`, {
        session_id: sessionId,
        sdp: base64EncodedCompleteOffer,
      });

      console.log(
        `[WebRTC] Received SDP answer from server, setting remote description...`
      );

      if (
        organisationId === "c126b1bb9b31f3de546ad8b476623d1991b4dcb2" ||
        organisationId === "365c65c6cf6e60de6f895693b30da2d368b30dbf"
      ) {
        const answerPayloadStringFromServer =
          serverDescriptionResponse.data.sdp;
        console.log(`[WebRTC] Received answer payload string from server`);

        if (typeof answerPayloadStringFromServer !== "string") {
          throw new Error(
            "Answer payload from server is not a string. Expected a JSON string."
          );
        }

        let parsedAnswerPayload;
        try {
          parsedAnswerPayload = JSON.parse(answerPayloadStringFromServer);
        } catch (e: any) {
          throw new Error(
            `Failed to parse answer payload JSON string: ${e.message}`
          );
        }

        console.log(`[WebRTC] Parsed answer payload`);

        if (typeof parsedAnswerPayload?.sdp !== "string") {
          throw new Error(
            "Answer SDP string is missing or not a string in parsed server response payload."
          );
        }

        const rawAnswerSdpString = parsedAnswerPayload.sdp;
        const answerSdpInit: RTCSessionDescriptionInit = {
          type: "answer",
          sdp: rawAnswerSdpString,
        };

        await pc.setRemoteDescription(new RTCSessionDescription(answerSdpInit));
        console.log(`[WebRTC] ✅ Remote description set successfully`);
      } else {
        const serverDescription = serverDescriptionResponse.data.sdp;
        console.log(
          `[WebRTC] Setting remote description from base64 encoded answer`
        );
        await pc.setRemoteDescription(
          new RTCSessionDescription(JSON.parse(atob(serverDescription)))
        );
        console.log(`[WebRTC] ✅ Remote description set successfully`);
      }
    }

    const deviceDimensionsResponse: AxiosResponse = await retryOperation(
      async (_) =>
        await axios.get(
          `${instanceIp}/interaction/get_dimensions/${sessionId}`
        ),
      5000,
      10
    );

    const deviceDimensions: { width: number; height: number } =
      deviceDimensionsResponse.data;

    const simulatorSession: SimulatorSession = {
      serverUrl: instanceIp,
      info: sessionInfo.data,
      sessionId: sessionId!,
      startedAt: new Date().getTime(),
      device: {
        screen: deviceDimensions,
        platform,
        name: sessionResponse.data.device_name,
        os: sessionResponse.data.device_os,
      },
      checkIfSessionIsRunning: async () => {
        const sessionStatusResponse = await axios.get(
          `${instanceIp}/session/${sessionId}`
        );
        return sessionStatusResponse.data.state === "assigned";
      },
      getLogsStore: () => {
        return logsStore;
      },
      getNetworkLogsStore: () => {
        return networkLogsStore;
      },
      stop: async () => {
        await cleanupSession({
          sessionId,
          socket,
          pc,
          serverUrl: instanceIp,
          useSocketIO,
          organisationId,
          onCleanup,
          storesToReset: [logsStore, networkLogsStore],
        });
      },
      getScreenshot: async () => {
        if (useSocketIO) {
          if (!latestSocketIOScreenshot) {
            const waitStart = Date.now();
            const maxWaitMs = 500;
            const pollIntervalMs = 50;
            while (
              !latestSocketIOScreenshot &&
              Date.now() - waitStart < maxWaitMs
            ) {
              await new Promise((resolve) =>
                setTimeout(resolve, pollIntervalMs)
              );
            }
            if (!latestSocketIOScreenshot) {
              console.warn(
                `[Screenshot] No Socket.IO frame after ${maxWaitMs}ms, falling back to HTTP endpoint`
              );
            }
          }
          if (latestSocketIOScreenshot) {
            console.log("[Screenshot] Using stored Socket.IO screenshot");
            const bytes = new Uint8Array(latestSocketIOScreenshot);
            return uint8ArrayToBase64(bytes);
          }
        }
        const screenshotResponse = await axios.get(
          `${instanceIp}/interaction/screen_shot/${sessionId}`
        );
        return `data:image/jpg;base64, ${screenshotResponse.data}`;
      },
      getUI: async () => {
        const pageSourceResponse = await axios.get(
          `${instanceIp}/interaction/page_source/${sessionId}`
        );
        return pageSourceResponse.data;
      },
      launchApp,
      commands: {
        biometry: async (match: boolean) => {
          match
            ? await axios.post(
                `${instanceIp}/interaction/biometric/match/${sessionId}`
              )
            : await axios.post(
                `${instanceIp}/interaction/biometric/nomatch/{session_id}/${sessionId}`
              );
        },
        zoom: async (direction: "in" | "out") => {
          const isAndroid = platform.toLowerCase() === "android";
          await axios.post(`${instanceIp}/interaction/zoom/${sessionId}`, {
            zoom_in: direction === "in",
            distance: isAndroid ? 300 : 5,
          });
        },
        shakeDevice: async () => {
          await axios.post(`${instanceIp}/interaction/shake/${sessionId}`);
        },
        openDeepLinkUrl: async (url: string) => {
          await axios.post(`${instanceIp}/interaction/open_link/${sessionId}`, {
            url,
          });
        },
        openAppClipLink: async (url: string) => {
          await axios.post(
            `${instanceIp}/interaction/open_app_clip_link/${sessionId}`,
            { url }
          );
        },
        playAudio: async (url: string) => {
          await axios.post(
            `${instanceIp}/interaction/play_audio/${sessionId}`,
            {
              url,
            }
          );
        },
        playVoice: async (text: string) => {
          const response = await axios.post(
            `${instanceIp}/interaction/speak/${sessionId}`,
            {
              text,
            }
          );
          const unparsedUrl = response.data.url as string;
          return `${instanceIp}/${unparsedUrl}`;
        },
        listen: async () => {
          const response = await axios.post(
            `${instanceIp}/interaction/listen`,
            {
              pre_roll_s: 10,
              timeout_s: 15,
              silence_min_ms: 700,
              vad_mode: 2,
            }
          );
          const text = response.data?.text ?? "";
          const unparsedUrl = response.data.audio_url as string;

          return {
            text,
            audioUrl: `${instanceIp}/${unparsedUrl}`,
          };
        },
        checkAudio: async (seconds: number = 3) => {
          const response = await axios.get(
            `${instanceIp}/interaction/check_audio/${sessionId}`,
            { params: { seconds } }
          );
          return response.data?.result === true;
        },
        tap: async ({
          coordinates,
          duration,
        }: {
          coordinates: { x: number; y: number };
          duration: number;
        }) => {
          await axios.post(`${instanceIp}/interaction/tap/${sessionId}`, {
            position: [coordinates.x, coordinates.y],
            duration,
          });
        },
        multiTap: async ({
          coordinates,
          count,
        }: {
          coordinates: { x: number; y: number };
          count: number;
        }) => {
          await axios.post(`${instanceIp}/interaction/multi_tap/${sessionId}`, {
            position: [coordinates.x, coordinates.y],
            count,
          });
        },
        setLocation: async (coordinates: Coordinates) => {
          await axios.post(
            `${instanceIp}/interaction/set_location/${sessionId}`,
            {
              latitude: coordinates.latitude,
              longitude: coordinates.longitude,
            }
          );
        },
        tapOnElement: async ({
          text,
          id,
          duration,
        }: {
          text?: string;
          id?: string;
          duration: number;
        }) => {
          const response = await axios.post(
            `${instanceIp}/interaction/tap_element/${sessionId}`,
            {
              text,
              id,
              duration,
            }
          );
          if (
            response?.data != null &&
            response.data.message === "ELEMENT_NOT_FOUND"
          ) {
            const identifier = text ?? id ?? "unknown";
            throw new Error(`Element [${identifier}] not found!`);
          }
          return response.data?.coordinates
            ? {
                x: response.data.coordinates[0],
                y: response.data.coordinates[1],
              }
            : null;
        },
        doubleTap: async ({
          coordinates,
          duration,
          count = 1,
        }: {
          coordinates: { x: number; y: number };
          duration: number;
          count: number;
        }) => {
          await axios.post(
            `${instanceIp}/interaction/double_tap/${sessionId}`,
            {
              position: [coordinates.x, coordinates.y],
              duration,
              count,
            }
          );
        },
        longPress: async ({
          coordinates,
          duration,
          count = 1,
        }: {
          coordinates: { x: number; y: number };
          duration: number;
          count: number;
        }) => {
          await axios.post(
            `${instanceIp}/interaction/long_press/${sessionId}`,
            {
              position: [coordinates.x, coordinates.y],
              duration,
              count,
            }
          );
        },
        swipe: async ({
          startCoordinates,
          endCoordinates,
          duration,
        }: {
          startCoordinates: { x: number; y: number };
          endCoordinates: { x: number; y: number };
          duration?: number;
        }) => {
          await axios.post(`${instanceIp}/interaction/swipe/${sessionId}`, {
            start: [
              Math.floor(startCoordinates.x),
              Math.floor(startCoordinates.y),
            ],
            end: [Math.floor(endCoordinates.x), Math.floor(endCoordinates.y)],
            ...(duration != null && { duration }),
          });
        },
        type: async ({ text }: { text: string }) => {
          await axios.post(`${instanceIp}/interaction/text/${sessionId}`, {
            text,
          });
        },
        keyStroke: async ({ keycode }: { keycode: number }) => {
          await axios.post(`${instanceIp}/interaction/keystroke/${sessionId}`, {
            keycode,
          });
        },
        tapHomeScreen: async () => {
          await axios.get(`${instanceIp}/interaction/home_button/${sessionId}`);
        },
        restartApp: async () => {
          await axios.post(
            `${instanceIp}/interaction/restart_app/${sessionId}`
          );
        },
        terminateApp: async () => {
          await axios.post(
            `${instanceIp}/interaction/terminate_app/${sessionId}`
          );
        },
      },
      setSubscriber: (subscriber: Subscriber<unknown>) => {
        nonNullSubscriber = subscriber;
      },
      subscriber: nonNullSubscriber,
      on: (event: "video", callback: (payload: VideoFramePayload) => void) => {
        if (event === "video") {
          videoFrameListeners.add(callback);
        }
        return simulatorSession;
      },
      off: (event: "video", callback: (payload: VideoFramePayload) => void) => {
        if (event === "video") {
          videoFrameListeners.delete(callback);
        }
        return simulatorSession;
      },
    } satisfies SimulatorSession;

    return simulatorSession;
  } catch (e) {
    console.log("e", e);
    if (socket) {
      socket.disconnect();
    }
    if (pc) {
      pc.close();
    }
    await axios.post(`${instanceIp}/stopSession`, {
      session_id: sessionId,
    });
    throw e;
  }
};

export { sessionMutationAtom, type SimulatorSession, type Device };
